import React, { useState, useEffect } from 'react';
import { Filter, Search, Star, Lock, ArrowLeft, Users, DollarSign, TrendingUp, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePageTracking } from '../../lib/hooks/usePageTracking';
import { useLeagueMode } from '../../lib/hooks/useLeagueMode';
import { ModeAccessMessage } from '../shared/ModeAccessMessage';
import { useLeagueStore } from '../../lib/leagueStore';
import { useThemeStore } from '../../lib/themeStore';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

// Interface pour les joueurs avec info d'équipe
interface ExtendedPlayer {
  id: number;
  name: string;
  position: string;
  team: string;
  points: number;
  goals?: number;
  assists?: number;
  salary: number;
  status: 'available' | 'untouchable';
  isInjured?: boolean;
  isScratch?: boolean;
  tradingBloc?: boolean | null;
  isIntouchable?: boolean;
  leaguePlayerId?: number;
  teamName?: string;
  managerName?: string;
}

interface TradingTeam {
  id: number;
  teamName: string;
  managerName: string;
  availablePlayers: ExtendedPlayer[];
  untouchablePlayers: ExtendedPlayer[];
}

// Composant pour la vue globale
interface GlobalViewSectionProps {
  availablePlayers: ExtendedPlayer[];
  untouchablePlayers: ExtendedPlayer[];
  searchTerm: string;
  positionFilter: string;
  theme: string;
}

const GlobalViewSection: React.FC<GlobalViewSectionProps> = ({ 
  availablePlayers, 
  untouchablePlayers, 
  searchTerm, 
  positionFilter,
  theme
}) => {
  // Filtrer les joueurs selon les critères de recherche
  const filterPlayers = (players: ExtendedPlayer[]) => {
    return players.filter(player => {
      const matchesSearch = !searchTerm || 
        player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        player.team.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (player.teamName && player.teamName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (player.managerName && player.managerName.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesPosition = positionFilter === 'all' || player.position === positionFilter;
      
      return matchesSearch && matchesPosition;
    });
  };

  const filteredAvailable = filterPlayers(availablePlayers);
  const filteredUntouchable = filterPlayers(untouchablePlayers);

  const getPlayerImageUrl = (player: ExtendedPlayer) => {
    return `https://assets.nhle.com/mugs/nhl/20242025/${player.team}/${player.id}.png`;
  };

  const PlayerRow: React.FC<{ player: ExtendedPlayer; isAvailable: boolean }> = ({ player, isAvailable }) => (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <div className="me-2">
            <img
              src={getPlayerImageUrl(player)}
              alt={player.name}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: isAvailable ? '2px solid #ffc107' : '2px solid #dc3545'
              }}
              onError={(e) => {
                e.currentTarget.src = "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
              }}
            />
          </div>
          <div>
            <div className="fw-bold small">{player.name}</div>
            <small className="d-none d-md-block">{player.managerName || 'Inconnu'}</small>
          </div>
        </div>
      </td>
      <td><span className="badge bg-primary small">{player.position}</span></td>
      <td className="small">
        <span>{player.managerName || 'Inconnu'}</span>
      </td>
      <td className="text-center small">{player.position !== 'G' ? (player.goals || 0) : '-'}</td>
      <td className="text-center small">{player.position !== 'G' ? (player.assists || 0) : '-'}</td>
      <td className="text-center small">{player.position !== 'G' ? player.points : '-'}</td>
      <td className="small">{(player.salary / 1000000).toFixed(1)}M$</td>
      <td className="small">{player.team}</td>
    </tr>
  );

  return (
    <div className="row">
      {/* Joueurs disponibles */}
      <div className="col-12 col-lg-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-warning bg-opacity-10 border-0">
            <h5 className="card-title mb-0 d-flex align-items-center">
              <Star className="text-warning me-2" size={20} fill="currentColor" />
              Joueurs disponibles ({filteredAvailable.length})
            </h5>
          </div>
          <div className="card-body p-0">
            {filteredAvailable.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Joueur</th>
                        <th>Pos.</th>
                        <th>Propriétaire</th>
                        <th className="text-center">B</th>
                        <th className="text-center">P</th>
                        <th className="text-center">Pts</th>
                        <th>Salaire</th>
                        <th>Équipe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAvailable.map(player => (
                        <PlayerRow key={player.id} player={player} isAvailable={true} />
                      ))}
                    </tbody>
                  </table>
                </div>
            ) : (
              <div className="text-center p-4">
                <Star className="text-muted mb-2" size={32} />
                <p className="text-muted mb-0">Aucun joueur disponible trouvé</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Joueurs intouchables */}
      <div className="col-12 col-lg-6 mb-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-header bg-danger bg-opacity-10 border-0">
            <h5 className="card-title mb-0 d-flex align-items-center">
              <Lock className="text-danger me-2" size={20} />
              Joueurs intouchables ({filteredUntouchable.length})
            </h5>
          </div>
          <div className="card-body p-0">
            {filteredUntouchable.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Joueur</th>
                        <th>Pos.</th>
                        <th>Propriétaire</th>
                        <th className="text-center">B</th>
                        <th className="text-center">P</th>
                        <th className="text-center">Pts</th>
                        <th>Salaire</th>
                        <th>Équipe</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUntouchable.map(player => (
                        <PlayerRow key={player.id} player={player} isAvailable={false} />
                      ))}
                    </tbody>
                  </table>
                </div>
            ) : (
              <div className="text-center p-4">
                <Lock className="text-muted mb-2" size={32} />
                <p className="text-muted mb-0">Aucun joueur intouchable trouvé</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const TradingBloc: React.FC = () => {
  // Tracker cette page
  usePageTracking('landingpage');

  const { isStandardFlexible, isLoading: modeLoading } = useLeagueMode();

  const navigate = useNavigate();
  const { selectedLeague } = useLeagueStore();
  const { theme } = useThemeStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState('all');
  const [tradingTeams, setTradingTeams] = useState<TradingTeam[]>([]);
  const [loading, setLoading] = useState(true);

  // Forcer le scroll en haut immédiatement au montage
  useEffect(() => {
    // Scroll immédiat
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Empêcher tout scroll automatique pendant les 2 premières secondes
    const preventScroll = (e: Event) => {
      e.preventDefault();
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    };

    // Bloquer le scroll pendant 2 secondes
    window.addEventListener('scroll', preventScroll, { passive: false });
    
    const timeout = setTimeout(() => {
      window.removeEventListener('scroll', preventScroll);
    }, 2000);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', preventScroll);
    };
  }, []);

  // Charger les données au montage du composant
  useEffect(() => {
    if (!selectedLeague) {
      navigate('/dashboard');
      return;
    }

    loadTradingData();
  }, [selectedLeague, navigate]);

  // S'assurer que le scroll reste en haut même après le chargement des données
  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      }, 100);
    }
  }, [loading]);

  // Fonction pour charger les données du Trading Bloc (optimisée)
  const loadTradingData = async () => {
    if (!selectedLeague?.id) return;
    
    try {
      setLoading(true);
      
      // Récupérer directement tous les joueurs disponibles et intouchables via une seule requête
      const { data: leaguePlayersData, error: leagueError } = await supabase
        .from('league_players_user')
        .select(`
          id,
          player_id,
          user_id,
          trading_bloc,
          is_intouchable,
          is_injured,
          is_scratch
        `)
        .eq('league_id', selectedLeague.id)
        .or('trading_bloc.eq.true,is_intouchable.eq.true');

      if (leagueError) throw leagueError;

      if (!leaguePlayersData || leaguePlayersData.length === 0) {
        setTradingTeams([]);
        return;
      }

      // Récupérer les IDs uniques des joueurs
      const playerIds = [...new Set(leaguePlayersData.map(p => p.player_id).filter(id => id != null))];
      
      // Récupérer les détails des joueurs en parallèle
      const [playersResult, goaliesResult, usersResult] = await Promise.all([
        supabase
          .from('players')
          .select('playerid, skaterfullname, positioncode, teamabbrevs, points, goals, assists, salary')
          .in('playerid', playerIds),
        supabase
          .from('goalies')
          .select('playerid, goaliefullname, teamabbrevs, points, salary')
          .in('playerid', playerIds),
        supabase
          .from('users')
          .select('user_id, username')
          .in('user_id', [...new Set(leaguePlayersData.map(p => p.user_id))])
      ]);

      // Créer des maps pour accès rapide
      const playersMap = new Map(playersResult.data?.map(p => [parseInt(p.playerid), p]) || []);
      const goaliesMap = new Map(goaliesResult.data?.map(g => [parseInt(g.playerid), g]) || []);
      const usersMap = new Map(usersResult.data?.map(u => [u.user_id, u.username]) || []);

      // Grouper par équipe
      const teamGroups = new Map<number, typeof leaguePlayersData>();
      leaguePlayersData.forEach(player => {
        if (!teamGroups.has(player.user_id)) {
          teamGroups.set(player.user_id, []);
        }
        teamGroups.get(player.user_id)!.push(player);
      });

      // Créer la structure finale
      const teams: TradingTeam[] = Array.from(teamGroups.entries()).map(([userId, players]) => {
        const username = usersMap.get(userId) || 'Inconnu';
        
        const mappedPlayers = players.map(lp => {
          const playerDetail = playersMap.get(lp.player_id);
          const goalieDetail = goaliesMap.get(lp.player_id);
          
          if (playerDetail) {
            return {
              id: lp.player_id,
              name: playerDetail.skaterfullname,
              position: playerDetail.positioncode,
              team: playerDetail.teamabbrevs,
              points: playerDetail.points || 0,
              goals: playerDetail.goals || 0,
              assists: playerDetail.assists || 0,
              salary: playerDetail.salary || 0,
              teamName: username,
              managerName: username,
              status: lp.is_intouchable ? 'untouchable' as const : 'available' as const,
              isInjured: lp.is_injured || false,
              isScratch: lp.is_scratch || false,
              tradingBloc: lp.trading_bloc,
              isIntouchable: lp.is_intouchable || false,
              leaguePlayerId: lp.id
            };
          } else if (goalieDetail) {
            return {
              id: lp.player_id,
              name: goalieDetail.goaliefullname,
              position: 'G',
              team: goalieDetail.teamabbrevs,
              points: goalieDetail.points || 0,
              salary: goalieDetail.salary || 0,
              teamName: username,
              managerName: username,
              status: lp.is_intouchable ? 'untouchable' as const : 'available' as const,
              isInjured: lp.is_injured || false,
              isScratch: lp.is_scratch || false,
              tradingBloc: lp.trading_bloc,
              isIntouchable: lp.is_intouchable || false,
              leaguePlayerId: lp.id
            };
          }
          return null;
        }).filter(p => p !== null) as ExtendedPlayer[];

        return {
          id: userId,
          teamName: username,
          managerName: username,
          availablePlayers: mappedPlayers.filter(p => p.tradingBloc === true && !p.isIntouchable),
          untouchablePlayers: mappedPlayers.filter(p => p.isIntouchable === true)
        };
      });

      setTradingTeams(teams);
    } catch (error) {
      console.error('Error loading trading data:', error);
      toast.error('Erreur lors du chargement des données de trading');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour obtenir tous les joueurs disponibles (vue globale)
  const getAllAvailablePlayers = (): ExtendedPlayer[] => {
    const allPlayers: ExtendedPlayer[] = [];
    tradingTeams.forEach(team => {
      allPlayers.push(...team.availablePlayers.map(player => ({
        ...player,
        teamName: team.teamName,
        managerName: team.managerName
      })));
    });
    
    // Séparer les joueurs et les gardiens
    const players = allPlayers.filter(player => player.position !== 'G');
    const goalies = allPlayers.filter(player => player.position === 'G');
    
    // Trier les joueurs par points (du plus haut au plus bas)
    players.sort((a, b) => (b.points || 0) - (a.points || 0));
    
    // Trier les gardiens par points aussi
    goalies.sort((a, b) => (b.points || 0) - (a.points || 0));
    
    // Retourner les joueurs triés suivis des gardiens
    return [...players, ...goalies];
  };

  // Fonction pour obtenir tous les joueurs intouchables (vue globale)
  const getAllUntouchablePlayers = (): ExtendedPlayer[] => {
    const allUntouchables: ExtendedPlayer[] = [];
    tradingTeams.forEach(team => {
      allUntouchables.push(...team.untouchablePlayers.map(player => ({
        ...player,
        teamName: team.teamName,
        managerName: team.managerName
      })));
    });
    
    // Séparer les joueurs et les gardiens
    const players = allUntouchables.filter(player => player.position !== 'G');
    const goalies = allUntouchables.filter(player => player.position === 'G');
    
    // Trier les joueurs par points (du plus haut au plus bas)
    players.sort((a, b) => (b.points || 0) - (a.points || 0));
    
    // Trier les gardiens par points aussi
    goalies.sort((a, b) => (b.points || 0) - (a.points || 0));
    
    // Retourner les joueurs triés suivis des gardiens
    return [...players, ...goalies];
  };



  const formatSalary = (salary: number) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(salary);
  };

  // Mode guard
  if (!modeLoading && isStandardFlexible) {
    return (
      <ModeAccessMessage
        variant="dynasty-only"
        title="Trading Bloc — Mode Dynasty / Keeper uniquement"
        message="Le bloc des échanges est une fonctionnalité du mode Dynasty/Keeper. En mode Standard Flexible, les échanges ne sont pas disponibles."
      />
    );
  }

  return (
    <div className="card shadow-lg border-0" style={{ scrollBehavior: 'auto' }}>
      <div className="card-body p-3 p-md-5" style={{ scrollMarginTop: 0 }}>
        {/* Header avec bouton retour */}
        <div className="mb-4">
          <button
            className="btn btn-link p-0 text-decoration-none"
            onClick={() => navigate('/my-office')}
            title="Retour au bureau"
          >
            <ArrowLeft size={18} className="me-2" />
            Retour
          </button>
        </div>

        {/* Titre principal */}
        <div className="text-center mb-4 mb-md-5">
          <div className="d-flex justify-content-center align-items-center mb-3">
            <div 
              className="d-flex align-items-center justify-content-center me-3"
              style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(45deg, #007bff, #0056b3)',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)'
              }}
            >
              <TrendingUp size={24} style={{ color: 'white' }} />
            </div>
            <h1 className="display-6 display-md-5 fw-bold mb-0">Trading Bloc</h1>
          </div>
          <p className="text-muted lead d-none d-md-block">
            Découvrez tous les joueurs disponibles et intouchables de la ligue
          </p>
          <p className="text-muted d-md-none">
            Vue globale des joueurs
          </p>
        </div>

        {/* Filtres et Recherche */}
        <div className="row g-3 mb-4">
          <div className="col-12 col-md-5">
            <div className="position-relative">
              <Search className="position-absolute" size={18} style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }} />
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Rechercher un joueur, une équipe NHL ou un GM..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-12 col-md-3">
            <select
              className="form-select"
              value={positionFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPositionFilter(e.target.value)}
            >
              <option value="all">Toutes les positions</option>
              <option value="C">Centre (C)</option>
              <option value="L">Ailier gauche (L)</option>
              <option value="R">Ailier droit (R)</option>
              <option value="D">Défenseur (D)</option>
              <option value="G">Gardien (G)</option>
            </select>
          </div>
          <div className="col-6 col-md-2">
            <button 
              className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
              onClick={loadTradingData}
              disabled={loading}
              title="Recharger les données du Trading Bloc"
            >
              <RefreshCw size={16} className="me-1 d-none d-sm-inline" />
              <span className="d-none d-sm-inline">Actualiser</span>
              <RefreshCw size={16} className="d-sm-none" />
            </button>
          </div>
          <div className="col-6 col-md-2">
            <button 
              className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
              onClick={() => {
                setSearchTerm('');
                setPositionFilter('all');
              }}
            >
              <Filter className="me-1 d-none d-sm-inline" size={18} />
              <span className="d-none d-sm-inline">Réinitialiser</span>
              <Filter className="d-sm-none" size={18} />
            </button>
          </div>
        </div>

        {/* Contenu principal */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="text-muted mt-3">Chargement des données de trading...</p>
          </div>
        ) : (
          <GlobalViewSection 
            availablePlayers={getAllAvailablePlayers()}
            untouchablePlayers={getAllUntouchablePlayers()}
            searchTerm={searchTerm}
            positionFilter={positionFilter}
            theme={theme}
          />
        )}

        {/* Actions en bas */}
        <div className="text-center mt-4 mt-md-5 pt-3 pt-md-4 border-top">
          <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
            <button 
              className="btn btn-primary btn-lg px-3 px-md-4 py-2 py-md-3 fw-bold"
              onClick={() => navigate('/trade-player')}
            >
              <TrendingUp size={18} className="me-2" />
              <span className="d-none d-sm-inline">Proposer un Échange</span>
              <span className="d-sm-none">Échanger</span>
            </button>
            <button 
              className="btn btn-outline-primary btn-lg px-3 px-md-4 py-2 py-md-3 fw-bold"
              onClick={() => alert('Fonctionnalité à venir')}
            >
              <Star size={18} className="me-2" />
              <span className="d-none d-sm-inline">Mes Préférences de Trading</span>
              <span className="d-sm-none">Préférences</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingBloc;
